import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Wrench, Eye, Building2, Info, AlertCircle, RefreshCw, Search, Filter, ChevronDown, Activity, Layers3, Settings2, UserRound, ArrowUpDown, X, ChevronRight, Sparkles, Lock } from 'lucide-react';
import { User } from '../../../../models/auth';
import { useCSVData, CSVRow, normalizeFuncionalidadValue } from '../hooks/useCSVData';
import LoadingSpinner from '../components/SMLoadingSpinner';
import logoCEU from '../../../../assets/images/GPT_CEU-logoBlanco.png';
import footerImg from '../../../../assets/images/CEU-UCH_footer-blanco.png';

interface HomePageProps {
  user: User;
}

interface ServiceAccess {
  service: CSVRow;
  roles: ('Responsable' | 'Técnico' | 'Revisor/a' | 'Administrador')[];
}

function getFieldValue(row: CSVRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function getServiceName(row: CSVRow): string {
  return getFieldValue(row, ['NombreProceso', 'Nombre del servicio']);
}

function getServiceEstado(row: CSVRow): string {
  return getFieldValue(row, ['Estado_implementacion', 'Estado implementación']);
}

function isCancelledService(row: CSVRow): boolean {
  return getServiceEstado(row).toLocaleLowerCase('es').trim() === 'cancelado';
}

function getModifiedDateValue(row: CSVRow): string {
  return getFieldValue(row, ['Modificado']);
}

function parseModifiedDate(raw: string | undefined): Date | null {
  if (!raw || !raw.trim()) return null;
  const value = raw.trim();

  const ddmmyyyy = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (ddmmyyyy) {
    const [, d, m, y, hh = '0', mm = '0', ss = '0'] = ddmmyyyy;
    const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function getServiceCentro(row: CSVRow): string {
  return getFieldValue(row, ['Centro']);
}

function isGptCeuService(row: CSVRow): boolean {
  const funcionalidad = normalizeFuncionalidadValue(getFieldValue(row, ['Funcionalidad'])).toLowerCase();
  return funcionalidad === 'gpt-ceu';
}

function getServiceId(row: CSVRow): string {
  const preferredKeys = [
    'ID',
    'ID servicio',
    'Id servicio',
    'id servicio',
    'ID del servicio',
    'Id del servicio',
    'id del servicio',
    'service_id',
    'servicio_id',
  ];

  for (const key of preferredKeys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  const dynamicKey = Object.keys(row).find((key) => {
    const normalized = key.toLowerCase();
    return normalized === 'id' || (normalized.includes('id') && normalized.includes('servicio'));
  });

  if (dynamicKey) {
    const value = row[dynamicKey];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

/** Extrae los emails de un campo JSON con estructura [{"nombre":"...", "user_mail":"..."}] */
function extractEmails(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      return arr
        .map((item: { user_mail?: string }) => item.user_mail?.toLowerCase().trim())
        .filter(Boolean) as string[];
    }
  } catch {
    // campo no es JSON válido
  }
  return [];
}

/** Color del badge de estado */
function estadoClass(estado: string | undefined): string {
  if (!estado) return '';
  const lower = estado.toLowerCase().trim();
  if (lower.includes('producción') || lower.includes('produccion') || lower.includes('activo') || lower.includes('completado'))
    return 'estado-activo';
  if (lower.includes('desarrollo') || lower.includes('progreso') || lower.includes('implementa'))
    return 'estado-desarrollo';
  if (lower.includes('pendiente') || lower.includes('parado') || lower.includes('pausa'))
    return 'estado-pendiente';
  return 'estado-default';
}

const HomePage: React.FC<HomePageProps> = ({ user }) => {
  const { data, loading, error } = useCSVData();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('TODOS');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRol, setFilterRol] = useState('TODOS');
  const [filterEstado, setFilterEstado] = useState('TODOS');
  const [filterFuncionalidad, setFilterFuncionalidad] = useState<'TODOS' | 'GPT_CEU' | 'OTROS'>('TODOS');
  const [filterConfigurable, setFilterConfigurable] = useState<'TODOS' | 'SI' | 'NO'>('TODOS');
  const [filterAcceso, setFilterAcceso] = useState<'TODOS' | 'DIRECTO' | 'ADMIN'>('TODOS');
  const [modifiedSortOrder, setModifiedSortOrder] = useState<'desc' | 'asc'>('desc');

  const userEmail = user.email.toLowerCase().trim();

  // Comprobar si es Administrador Global
  const isAdmin = useMemo(() => {
    try {
      const raw = process.env.REACT_APP_SM_ACCESAD_LIST;
      if (!raw) return false;
      const list = JSON.parse(raw);
      if (Array.isArray(list)) {
        return list.map((e: string) => e.toLowerCase().trim()).includes(userEmail);
      }
    } catch {
      return false;
    }
    return false;
  }, [userEmail]);

  // Filtrar servicios donde el usuario aparece como Responsable o Técnico, o mostrar todo si es Admin
  const userServices: ServiceAccess[] = useMemo(() => {
    return data
      .filter((row) => !isCancelledService(row))
      .map((row): ServiceAccess | null => {
        const roles: ('Responsable' | 'Técnico' | 'Revisor/a' | 'Administrador')[] = [];

        const responsables = extractEmails(row['Responsable servicio']);
        const tecnicos = extractEmails(row['Técnico']);
        const revisores = extractEmails(row['Revisor/a']);

        if (responsables.includes(userEmail)) roles.push('Responsable');
        if (tecnicos.includes(userEmail)) roles.push('Técnico');
        if (revisores.includes(userEmail)) roles.push('Revisor/a');

        // Si es admin y no está directamente involucrado, le damos un rol nominal para que pueda verlo
        if (isAdmin && roles.length === 0) {
          roles.push('Administrador');
        }

        if (roles.length > 0) {
          return { service: row, roles };
        }
        return null;
      })
      .filter((item): item is ServiceAccess => item !== null);
  }, [data, userEmail, isAdmin]);

  // Extraer centros únicos para filtros dinámicos
  const centros = useMemo(() => {
    const unique = [
      ...new Set(
        userServices
          .map((s) => getServiceCentro(s.service))
          .filter(Boolean)
      ),
    ];
    return ['TODOS', ...unique.sort()];
  }, [userServices]);

  // Filtrar por categoría, rol, estado y búsqueda
  const filtered = useMemo(() => {
    const filteredServices = userServices.filter((s) => {
      // Centro filter
      if (activeCategory !== 'TODOS' && getServiceCentro(s.service) !== activeCategory) return false;

      // Rol filter
      if (filterRol !== 'TODOS' && !s.roles.includes(filterRol as any)) return false;

      // Estado filter
      if (filterEstado !== 'TODOS') {
        const estClass = estadoClass(getServiceEstado(s.service));
        if (estClass !== filterEstado) return false;
      }

      // Funcionalidad filter
      if (filterFuncionalidad !== 'TODOS') {
        const isGpt = isGptCeuService(s.service);
        if (filterFuncionalidad === 'GPT_CEU' && !isGpt) return false;
        if (filterFuncionalidad === 'OTROS' && isGpt) return false;
      }

      // Configurable filter
      if (filterConfigurable !== 'TODOS') {
        const isConfigurable = isGptCeuService(s.service);
        if (filterConfigurable === 'SI' && !isConfigurable) return false;
        if (filterConfigurable === 'NO' && isConfigurable) return false;
      }

      // Tipo de acceso (directo/admin) filter
      if (filterAcceso !== 'TODOS') {
        const hasDirectRole = s.roles.some((role) => role !== 'Administrador');
        if (filterAcceso === 'DIRECTO' && !hasDirectRole) return false;
        if (filterAcceso === 'ADMIN' && hasDirectRole) return false;
      }

      // Search filter
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const nombre = getServiceName(s.service).toLowerCase();
        if (!nombre.includes(q)) return false;
      }

      return true;
    });

    return [...filteredServices].sort((a, b) => {
      const aDate = parseModifiedDate(getModifiedDateValue(a.service));
      const bDate = parseModifiedDate(getModifiedDateValue(b.service));
      const aTs = aDate ? aDate.getTime() : Number.POSITIVE_INFINITY;
      const bTs = bDate ? bDate.getTime() : Number.POSITIVE_INFINITY;
      return modifiedSortOrder === 'asc' ? aTs - bTs : bTs - aTs;
    });
  }, [
    userServices,
    activeCategory,
    filterRol,
    filterEstado,
    filterFuncionalidad,
    filterConfigurable,
    filterAcceso,
    searchQuery,
    modifiedSortOrder,
  ]);

  // ── Loading ──
  if (loading) return <LoadingSpinner />;

  // ── Error ──
  if (error) {
    return (
      <div className="error-container">
        <AlertCircle size={48} className="error-icon" />
        <h2>Error al cargar los servicios</h2>
        <p>{error}</p>
        <button className="btn-primary" onClick={() => window.location.reload()}>
          <RefreshCw size={16} /> Reintentar
        </button>
      </div>
    );
  }

  const firstName = (user.name || '').split(' ')[0] || user.name || 'Usuario';

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 20 ? 'Buenas tardes' : 'Buenas noches';

  const countByRole = (role: 'Responsable' | 'Técnico' | 'Revisor/a') =>
    userServices.filter((s) => s.roles.includes(role)).length;

  return (
    <div className="hp-dashboard">
      {/* Personal Hero */}
      <section className="hp-hero">
        <div className="hp-hero-bg">
          <div className="hp-hero-orb hp-hero-orb-1" />
          <div className="hp-hero-orb hp-hero-orb-2" />
        </div>

        <div className="hp-hero-inner hp-hero-inner-centered">
          <img src={logoCEU} alt="GPT-CEU" className="hp-hero-logo" />

          <div className="hp-hero-text hp-hero-text-centered">
            <span className="hp-greeting">
              <Sparkles size={12} />
              {greeting}
            </span>
            <h1 className="hp-hero-title">
              Hola, <span className="hp-hero-name">{firstName}</span>
            </h1>
            <p className="hp-hero-desc">
              {isAdmin
                ? 'Tienes visión global como administrador. Explora y gestiona todos los servicios disponibles.'
                : 'Este es tu panel personal. Aquí encontrarás todos los servicios en los que participas.'}
            </p>
          </div>

          <div className="hp-hero-stats">
            <div className="hp-hero-stat">
              <span className="hp-stat-num">{userServices.length}</span>
              <span className="hp-stat-cap">Servicios</span>
            </div>
            <div className="hp-stat-sep" />
            <div className="hp-hero-stat">
              <span className="hp-stat-num">{countByRole('Responsable')}</span>
              <span className="hp-stat-cap">
                <Shield size={10} /> Responsable
              </span>
            </div>
            <div className="hp-stat-sep" />
            <div className="hp-hero-stat">
              <span className="hp-stat-num">{countByRole('Técnico')}</span>
              <span className="hp-stat-cap">
                <Wrench size={10} /> Técnico
              </span>
            </div>
            <div className="hp-stat-sep" />
            <div className="hp-hero-stat">
              <span className="hp-stat-num">{countByRole('Revisor/a')}</span>
              <span className="hp-stat-cap">
                <Eye size={10} /> Revisor/a
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <div className="lp-toolbar hp-toolbar">
        <div className="lp-search">
          <Search size={15} className="lp-search-icon" />
          <input
            type="text"
            placeholder="Buscar por nombre del servicio..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="lp-search-input"
          />
          {searchQuery && (
            <button
              className="lp-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Limpiar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="lp-toolbar-filters hp-toolbar-filters">
          <div className="lp-select">
            <Filter size={13} className="lp-select-icon" />
            <select
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
              aria-label="Filtrar por centro"
            >
              {centros.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'TODOS' ? 'Todos los centros' : cat}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Shield size={13} className="lp-select-icon" />
            <select
              value={filterRol}
              onChange={(e) => setFilterRol(e.target.value)}
              aria-label="Filtrar por rol"
            >
              <option value="TODOS">Todos los roles</option>
              <option value="Responsable">Responsable</option>
              <option value="Técnico">Técnico</option>
              <option value="Revisor/a">Revisor/a</option>
              {isAdmin && <option value="Administrador">Solo visión admin</option>}
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Activity size={13} className="lp-select-icon" />
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              aria-label="Filtrar por estado"
            >
              <option value="TODOS">Todos los estados</option>
              <option value="estado-activo">Activos</option>
              <option value="estado-desarrollo">Desarrollo</option>
              <option value="estado-pendiente">Pendientes</option>
              <option value="estado-default">Otros</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Layers3 size={13} className="lp-select-icon" />
            <select
              value={filterFuncionalidad}
              onChange={(e) => setFilterFuncionalidad(e.target.value as 'TODOS' | 'GPT_CEU' | 'OTROS')}
              aria-label="Filtrar por funcionalidad"
            >
              <option value="TODOS">Todas las funcionalidades</option>
              <option value="GPT_CEU">Solo GPT-CEU</option>
              <option value="OTROS">Solo otras</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <Settings2 size={13} className="lp-select-icon" />
            <select
              value={filterConfigurable}
              onChange={(e) => setFilterConfigurable(e.target.value as 'TODOS' | 'SI' | 'NO')}
              aria-label="Configurable"
            >
              <option value="TODOS">Configurable: todos</option>
              <option value="SI">Configurable: sí</option>
              <option value="NO">Configurable: no</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <UserRound size={13} className="lp-select-icon" />
            <select
              value={filterAcceso}
              onChange={(e) => setFilterAcceso(e.target.value as 'TODOS' | 'DIRECTO' | 'ADMIN')}
              aria-label="Tipo de acceso"
            >
              <option value="TODOS">Tipo de acceso: todos</option>
              <option value="DIRECTO">Acceso directo</option>
              <option value="ADMIN">Solo visión admin</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>

          <div className="lp-select">
            <ArrowUpDown size={13} className="lp-select-icon" />
            <select
              value={modifiedSortOrder}
              onChange={(e) => setModifiedSortOrder(e.target.value as 'desc' | 'asc')}
              aria-label="Ordenar por fecha"
            >
              <option value="desc">Más recientes</option>
              <option value="asc">Más antiguos</option>
            </select>
            <ChevronDown size={13} className="lp-select-arrow" />
          </div>
        </div>
      </div>

      {/* Section header */}
      <div className="hp-section-header">
        <div className="hp-section-title-group">
          <h2 className="hp-section-title">Tus servicios</h2>
          <span className="hp-section-count">{filtered.length}</span>
        </div>
        {isAdmin && (
          <span className="hp-admin-pill" title="Estás viendo esto como administrador global">
            <Shield size={12} />
            Visión administrador
          </span>
        )}
      </div>

      {/* Services grid */}
      {filtered.length === 0 ? (
        <div className="lp-empty">
          <div className="lp-empty-icon">
            <Info size={26} />
          </div>
          <h3>Sin servicios que mostrar</h3>
          <p>
            {userServices.length === 0
              ? 'No tienes servicios asignados actualmente.'
              : 'Prueba a ajustar los filtros o la búsqueda.'}
          </p>
        </div>
      ) : (
        <div className="hp-grid">
          {filtered.map((item, i) => {
            const { service, roles } = item;
            const nombre = getServiceName(service) || 'Sin nombre';
            const serviceId = getServiceId(service);
            const centro = getServiceCentro(service);
            const estado = getServiceEstado(service);
            const estadoCls = estadoClass(estado);
            const isClickable = isGptCeuService(service);

            const handleNavigate = () => {
              if (!isClickable) return;
              navigate(
                `/gestion-servicios/servicio/${encodeURIComponent(nombre)}${serviceId ? `?sid=${encodeURIComponent(serviceId)}` : ''}`
              );
            };

            return (
              <article
                key={i}
                className={`hp-card ${!isClickable ? 'hp-card-disabled' : ''}`}
                onClick={handleNavigate}
                onKeyDown={(e) => {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleNavigate();
                  }
                }}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : -1}
                aria-label={
                  isClickable
                    ? `Abrir configuración del servicio ${nombre}`
                    : `Servicio ${nombre} no disponible para configuración`
                }
              >
                <div className="hp-card-accent" data-estado={estadoCls || 'estado-default'} />

                <div className="hp-card-top">
                  {estado && (
                    <span className={`lp-badge lp-badge-estado ${estadoCls}`}>
                      <span className="lp-badge-dot" />
                      {estado}
                    </span>
                  )}
                  {centro && (
                    <span className="lp-chip lp-chip-sm">
                      <Building2 size={11} /> {centro}
                    </span>
                  )}
                </div>

                <h3 className="hp-card-title">{nombre}</h3>

                <div className="hp-card-roles">
                  {roles.map((role) => (
                    <span
                      key={role}
                      className={`hp-role-badge ${
                        role === 'Responsable'
                          ? 'role-responsable'
                          : role === 'Técnico'
                          ? 'role-tecnico'
                          : role === 'Revisor/a'
                          ? 'role-revisor'
                          : 'role-admin'
                      }`}
                    >
                      {role === 'Responsable' ? (
                        <Shield size={11} />
                      ) : role === 'Técnico' ? (
                        <Wrench size={11} />
                      ) : role === 'Revisor/a' ? (
                        <Eye size={11} />
                      ) : (
                        <Shield size={11} />
                      )}
                      {role === 'Administrador' ? 'Visión admin' : role}
                    </span>
                  ))}
                </div>

                <div className="hp-card-footer">
                  {isClickable ? (
                    <>
                      <span className="hp-card-cta">Configurar</span>
                      <ChevronRight size={14} className="hp-card-cta-icon" />
                    </>
                  ) : (
                    <span className="hp-card-locked">
                      <Lock size={12} /> No configurable
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="home-footer">
        <a href="https://gpt.ceu.es/" target="_blank" rel="noopener noreferrer" className="footer-link">
          <img src={footerImg} alt="CEU" className="footer-img" />
        </a>
      </div>
    </div>
  );
};

export default HomePage;
